import React, { memo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Table, TableBody, TableColumn, TableHeader, TableRow, Th } from 'Components/Table';
import { Icon } from 'Components/Icons';
import styles from './pdfFormTemplates.module.css';

const STATUS_CLASSES: Record<string, string> = {
  pending: styles.statusDraft,
  signed: styles.statusActive,
  void: '',
};

interface Props {
  submissions: any;
  fetching: boolean;
  onResend: (submission: any) => void;
  onDelete: (submission: any) => void;
  onViewPdf: (url: string) => void;
}

function PdfFormSubmissionsList({ submissions, fetching, onResend, onDelete, onViewPdf }: Props) {
  const data = submissions?.data || [];

  if (fetching && !data.length) {
    return <div className={styles.emptyState}>Loading...</div>;
  }

  if (data.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No form submissions yet.</p>
        <p>Send a form template to a client to create a submission.</p>
      </div>
    );
  }

  return (
    <Table className={`table ${styles.formListWrapper}`}>
      <TableHeader>
        <TableRow>
          <Th>Template</Th>
          <Th>Project</Th>
          <Th>Client</Th>
          <Th>Status</Th>
          <Th>Sent</Th>
          <Th>Signed</Th>
          <Th />
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((sub: any) => (
          <TableRow key={sub.id}>
            <TableColumn>
              <p className={styles.formName}>{sub.template?.name || '—'}</p>
            </TableColumn>
            <TableColumn>
              <p>{sub.project?.address?.address || sub.project?.uid || '—'}</p>
            </TableColumn>
            <TableColumn>
              <p>{sub.client_name}</p>
              <p style={{ fontSize: 12, color: '#777185' }}>{sub.client_email}</p>
            </TableColumn>
            <TableColumn>
              <span className={`${styles.statusBadge} ${STATUS_CLASSES[sub.status] || ''}`}>
                {sub.status}
              </span>
            </TableColumn>
            <TableColumn>
              <p>{sub.emailed_at ? new Date(sub.emailed_at).toLocaleDateString() : '—'}</p>
              {sub.emailed_at && (
                <p style={{ fontSize: 12, color: '#777185', margin: 0 }}>
                  {new Date(sub.emailed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
            </TableColumn>
            <TableColumn>
              <p>{sub.signed_at ? new Date(sub.signed_at).toLocaleDateString() : '—'}</p>
              {sub.signed_at && (
                <p style={{ fontSize: 12, color: '#777185', margin: 0 }}>
                  {new Date(sub.signed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
            </TableColumn>
            <TableColumn>
              <div className="d-flex align-items-center" style={{ gap: 12 }}>
                {sub.signed_url && (
                  <button type="button" className={styles.sendBtn} onClick={() => onViewPdf(sub.signed_url)}>
                    View Signed
                  </button>
                )}
                {sub.prefilled_url && !sub.signed_url && (
                  <button type="button" className={styles.sendBtn} onClick={() => onViewPdf(sub.prefilled_url)}>
                    View PDF
                  </button>
                )}
                {sub.status === 'pending' && (
                  <button type="button" className={styles.sendBtn} onClick={() => onResend(sub)}>
                    Resend
                  </button>
                )}
                <button
                  type="button"
                  className={styles.trashBtn}
                  aria-label={`Delete submission for ${sub.client_name}`}
                  onClick={() => onDelete(sub)}
                >
                  <Icon type="trash" />
                </button>
              </div>
            </TableColumn>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const PdfFormSubmissionsListMemo = memo(PdfFormSubmissionsList, areEqual);
export { PdfFormSubmissionsListMemo as PdfFormSubmissionsList };
